import React, { ChangeEventHandler, useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import { useRouter } from 'next/router'
import LazyHydrate from 'react-lazy-hydration'
import { Button, ButtonProps } from '@/atoms/Button'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { Input, InputProps } from '@/atoms/Input'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { AsDiv, ParagraphSM, TitleMD, TitleSM } from '@/atoms/Text'
import { IconColor } from '@/atoms/utils'
import { AD_TESTING_COOKIE } from '@/constants/cookies'
import { Durations } from '@/constants/durations'
import { AngelDialog } from '@/molecules/AngelDialog'
import { useToast } from '@/molecules/Toast'
import { useKonamiTapper } from '@/organisms/Footer/ExperimentVariantPicker/useKonamiTapper'
import { useOptimizelyFeatureList } from '@/organisms/Footer/ExperimentVariantPicker/useOptimizelyFeatureList'
import { useUser } from '@/services/UserService'
import { useSafeAnalytics } from '@/utils/analytics'
import { PickableOptimizelyExperiment } from './useOptimizelyFeatureList/useOptimizelyFeatureList'

export interface ExperimentVariantPickerProps {
  logoColor?: IconColor
}

const DEVELOPER_MODE_COOKIE_NAME = 'enable_developer_mode'

export const isDevMode = () => Boolean(getCookie(DEVELOPER_MODE_COOKIE_NAME))

const InternalExperimentVariantPicker: React.FC<ExperimentVariantPickerProps> = ({ logoColor = 'white' }) => {
  const { showToast } = useToast()
  const { isKonamiMaster, increaseTapCount } = useKonamiTapper({ enabled: isDevMode() })
  const [isDialogOpen, setDialogOpen] = useState(false)
  const { forceExperimentVariation, isExperimentSelected, experiments, configVersion } = useOptimizelyFeatureList()
  const { getState } = useSafeAnalytics()
  const { anonymousId } = getState().user
  const { userId } = useUser()
  const { reload } = useRouter()
  const [isForcingAd, setIsForcingAd] = useState(Boolean(getCookie(AD_TESTING_COOKIE)))
  const [filterString, setFilterString] = useState('')
  const [importString, setImportString] = useState('')
  const [{ filteredExperiments, numExperiments, numVisibleExperiments }, setExperiments] = useState({
    filteredExperiments: [] as PickableOptimizelyExperiment[],
    numExperiments: 0,
    numVisibleExperiments: 0,
  })

  useEffect(() => {
    const sortedExperiments = (experiments || []).sort((a, b) => a.experimentKey.localeCompare(b.experimentKey))
    const trimmedFilterString = filterString?.trim()?.toLowerCase()
    const filteredExperiments = trimmedFilterString
      ? sortedExperiments.filter((experiment) => experiment.experimentKey.toLowerCase().includes(trimmedFilterString))
      : sortedExperiments

    setExperiments({
      filteredExperiments,
      numExperiments: sortedExperiments.length,
      numVisibleExperiments: filteredExperiments.length,
    })
  }, [experiments, filterString])

  useEffect(() => {
    experiments?.forEach((experiment) => {
      const experimentCookieVariation = getCookie(experiment.experimentKey)
      if (
        typeof experimentCookieVariation === 'string' &&
        experimentCookieVariation &&
        !isExperimentSelected(experiment.experimentKey, experimentCookieVariation)
      ) {
        forceExperimentVariation(experiment.experimentKey, experimentCookieVariation)
      }
    })
  }, [forceExperimentVariation, isExperimentSelected, experiments])

  useEffect(() => {
    if (isKonamiMaster) {
      setCookie(DEVELOPER_MODE_COOKIE_NAME, true, { maxAge: Durations.ONE_WEEK_IN_SECONDS })
    }
  }, [isKonamiMaster])

  const handleClearPreviewClick = useCallback(() => {
    fetch('/api/contentful/clearPreview').then(() => reload())
  }, [reload])

  const handleForceAdsClick = useCallback(() => {
    if (isForcingAd) {
      deleteCookie(AD_TESTING_COOKIE)
      setIsForcingAd(false)
    } else {
      setCookie(AD_TESTING_COOKIE, 'force', { maxAge: Durations.ONE_DAY_IN_SECONDS })
      setIsForcingAd(true)
    }
  }, [isForcingAd])

  const setVariation = useCallback(
    (experimentKey: string, variant: string) => {
      setCookie(experimentKey, variant)
      forceExperimentVariation(experimentKey, variant)
    },
    [forceExperimentVariation],
  )

  const exportVariations = useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(experiments))
    showToast('Variations Copied Successfully', {
      shouldHideClose: true,
      icon: <CopyIcon size={14} color="white" />,
      showInMillis: 1400,
    })
  }, [experiments, showToast])

  const handleImport = useCallback(() => {
    let parsedVariations
    try {
      parsedVariations = JSON.parse(importString)
    } catch (error) {
      showToast('There was an error.  Please try again.', { shouldHideClose: true, showInMillis: 1400 })
      return
    }
    if (parsedVariations) {
      parsedVariations.map((variation: PickableOptimizelyExperiment) => {
        if (variation?.experimentKey && variation?.enabledVariation) {
          setVariation(variation.experimentKey, variation.enabledVariation)
        }
      })
    }
  }, [importString, setVariation, showToast])

  const importVariations = useCallback(() => {
    handleImport()
    setTimeout(() => {
      showToast('Variations Imported Successfully', {
        shouldHideClose: true,
        showInMillis: 1400,
      })
    }, 1)
  }, [handleImport, showToast])

  const applyGuildPreset = useCallback(() => setFilterString('guild_membership'), [])
  const applyPIFPreset = useCallback(() => setFilterString('pif'), [])
  const applyTicketPreset = useCallback(() => setFilterString('ticket'), [])

  const handleFilterChanged: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    setFilterString(e.target.value)
  }, [])

  return (
    <div className="flex flex-row items-center space-x-4" onClick={increaseTapCount}>
      <AngelLogo color={logoColor} />
      {isKonamiMaster ? (
        <>
          <div className="w-fit cursor-pointer text-lg" onClick={() => setDialogOpen(true)}>
            Developer
          </div>
          <AngelDialog
            isDarkMode
            open={isDialogOpen}
            className="!h-screen"
            bodyClassName="!h-[94%] !w-[94%] -translate-y-[53%] !my-0 rounded-2xl !bg-gray-900"
            onClose={() => {
              setDialogOpen(false)
              if (configVersion > 0) {
                reload()
              }
            }}
            title="Developer Tools"
          >
            <div className="flex h-[95%] w-full flex-col gap-4 overflow-x-hidden overflow-y-scroll px-1 scrollbar-thin scrollbar-track-[#2a2824] scrollbar-thumb-gray-800">
              <div className="flex flex-col items-center gap-4 lg:flex-row">
                <TitleMD className="min-w-[180px]">User Information:</TitleMD>
                <ParagraphSM as={AsDiv} color="gray-500">
                  Anonymous ID: {anonymousId}
                </ParagraphSM>
                {userId && (
                  <ParagraphSM as={AsDiv} color="gray-500">
                    User ID: {userId}
                  </ParagraphSM>
                )}
              </div>

              <div className="flex flex-col items-center gap-4 pt-1 lg:flex-row">
                <TitleMD className="min-w-[180px]">Custom Actions:</TitleMD>
                <StyledButton onClick={handleClearPreviewClick}>Clear Preview</StyledButton>
                <StyledButton onClick={handleForceAdsClick}>
                  {isForcingAd ? 'Stop Forcing Ads' : 'Force Ads in Video Player'}
                </StyledButton>
              </div>

              <div className="flex flex-col items-center gap-4 pt-1 lg:flex-row">
                <TitleMD className="min-w-[180px]">Export / Import:</TitleMD>
                <StyledButton onClick={exportVariations}>Export Current Experiment Variations</StyledButton>
                <StyledInput
                  placeholder="Experiment Variations to Import"
                  value={importString}
                  onChange={(e) => setImportString(e.target.value)}
                />
                <StyledButton onClick={importVariations}>Import</StyledButton>
              </div>

              <div className="flex flex-col items-center gap-4 pt-1 lg:flex-row">
                <TitleMD className="min-w-[180px]">Experiments:</TitleMD>
              </div>

              <div className="flex flex-col items-center gap-4 pt-1 lg:flex-row">
                <TitleSM className="ml-[10px] min-w-[170px]">Filter Presets:</TitleSM>
                <FilterPresetButton onClick={applyGuildPreset}>Guild Membership Experiments</FilterPresetButton>
                <FilterPresetButton onClick={applyPIFPreset}>PIF Experiments</FilterPresetButton>
                <FilterPresetButton onClick={applyTicketPreset}>Ticket Experiments</FilterPresetButton>
              </div>

              <div className="flex flex-col items-center gap-4 pt-1 lg:flex-row">
                <TitleSM className="ml-[10px] min-w-[170px]">Filter Experiments:</TitleSM>
                <StyledInput placeholder="Filter" value={filterString} onChange={handleFilterChanged} />
                <ParagraphSM as={AsDiv} color="gray-500">
                  Showing {numVisibleExperiments} of {numExperiments} experiments
                </ParagraphSM>
              </div>

              <div className="row-auto grid w-full grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-4">
                {filteredExperiments.map((optimizelyExperiment) => {
                  return (
                    <div
                      key={optimizelyExperiment.experimentKey}
                      className="shrink-0 rounded border border-gray-600 p-4 "
                    >
                      <div className="overflow-x-hidden text-ellipsis pb-2 font-bold">
                        {optimizelyExperiment.experimentKey}
                      </div>
                      {optimizelyExperiment.variations.sort().map((variant) => {
                        return (
                          <div
                            key={variant}
                            className={classNames(
                              'cursor-pointer px-4 pb-1 text-ellipsis overflow-x-hidden',
                              isExperimentSelected(optimizelyExperiment.experimentKey, variant)
                                ? 'text-oxide font-bold'
                                : 'text-gray-200',
                            )}
                            onClick={() => setVariation(optimizelyExperiment.experimentKey, variant)}
                          >
                            {variant}
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </div>
          </AngelDialog>
        </>
      ) : null}
    </div>
  )
}

const StyledButton: React.FC<Pick<ButtonProps, 'children' | 'onClick'>> = ({ children, onClick }) => {
  return (
    <Button className="justify-center text-gray-100 ring-transparent" variant="gray" onClick={onClick}>
      {children}
    </Button>
  )
}

const StyledInput: React.FC<Pick<InputProps, 'placeholder' | 'value' | 'onChange'>> = ({
  placeholder,
  value,
  onChange,
}) => {
  return (
    <Input
      containerClassName="!mt-0 w-full lg:w-96"
      inputClasses="w-full photon-paragraph-md rounded border-b-0 bg-core-gray-100 px-2 py-2.5 text-gray-600 focus-visible:border-b-0 ring-oxide"
      label=""
      usePlaceholderAsLabel={true}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  )
}

const FilterPresetButton: React.FC<Pick<ButtonProps, 'children' | 'onClick'>> = ({ children, onClick }) => {
  return (
    <Button className="justify-center ring-transparent" variant="oxide-bright" onClick={onClick}>
      {children}
    </Button>
  )
}

export const ExperimentVariantPicker: React.FC<ExperimentVariantPickerProps> = ({ logoColor }) => {
  if (isDevMode()) return <InternalExperimentVariantPicker logoColor={logoColor} />

  return (
    <LazyHydrate on="click">
      <InternalExperimentVariantPicker logoColor={logoColor} />
    </LazyHydrate>
  )
}
